import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, Save, Sparkles, Trash2, TriangleAlert, Unlink, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TimelineLinkPicker } from "@/components/timeline/timeline-link-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { StoryAttachmentView } from "@/features/story";
import type {
  TimelineEventDraft,
  TimelineEventView,
  TimelineLinkTargetOption,
} from "@/features/timeline";
import { cn } from "@/lib/utils";

type TimelineEventEditorProps = {
  allowDelete?: boolean;
  event: TimelineEventView | null;
  linkOptions: TimelineLinkTargetOption[];
  onAddLink: (option: TimelineLinkTargetOption) => Promise<void>;
  onClose?: () => void;
  onDelete: () => void;
  onGenerateImage: () => Promise<void>;
  onRemoveImage: () => Promise<void>;
  onRemoveLink: (linkId: string) => Promise<void>;
  onSave: (draft: TimelineEventDraft) => Promise<unknown>;
};

type SaveState = "error" | "idle" | "saving" | "success";

const textareaClassName =
  "min-h-[10rem] w-full resize-none border-0 border-b border-border/85 bg-transparent px-0 py-3 text-base text-foreground transition-colors placeholder:text-muted-foreground/90 focus-visible:outline-none";

export function TimelineEventEditor({
  allowDelete = true,
  event,
  linkOptions,
  onAddLink,
  onClose,
  onDelete,
  onGenerateImage,
  onRemoveImage,
  onRemoveLink,
  onSave,
}: TimelineEventEditorProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<TimelineEventDraft>({
    description: "",
    eraLabel: "",
    title: "",
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const selectedEventId = event?.id ?? null;
  const imageAttachments = useMemo<StoryAttachmentView[]>(
    () =>
      event?.imagePath
        ? [
            {
              id: event.imagePath,
              fileName: event.title,
              imageUrl: event.imageUrl ?? null,
              path: event.imagePath,
            },
          ]
        : [],
    [event?.imagePath, event?.imageUrl, event?.title],
  );

  useEffect(() => {
    if (!event) {
      setDraft({
        description: "",
        eraLabel: "",
        title: "",
      });
      setSaveError(null);
      setSaveState("idle");
      return;
    }

    setDraft({
      description: event.description,
      eraLabel: event.eraLabel,
      title: event.title,
    });
    setSaveError(null);
    setSaveState("idle");
  }, [selectedEventId, event]);

  async function handleSave() {
    if (!event || saveState === "saving") {
      return;
    }

    setSaveState("saving");
    setSaveError(null);
    try {
      await onSave(draft);
      setSaveState("success");
    } catch (error) {
      setSaveState("error");
      setSaveError(error instanceof Error ? error.message : "timeline.messages.saveError");
    }
  }

  if (!event) {
    return null;
  }

  return (
    <section className="space-y-8 border-t border-border/70 pt-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-clay">
            {t("timeline.editor.eyebrow")}
          </p>
          <h2 className="text-[2.5rem] leading-none tracking-tight text-foreground">
            {draft.title.trim() || event.title}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("timeline.list.position", { position: event.sortOrder + 1 })} /{" "}
            {t("timeline.list.linkSummary", { count: event.linkCount })}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {onClose ? (
            <Button onClick={onClose} type="button" variant="outline">
              <X className="h-4 w-4" />
              {t("timeline.editor.closeAction", { defaultValue: "Close editor" })}
            </Button>
          ) : null}
          {allowDelete ? (
            <Button onClick={onDelete} type="button" variant="destructive">
              <Trash2 className="h-4 w-4" />
              {t("timeline.actions.delete")}
            </Button>
          ) : null}
        </div>
      </div>

      {(saveError || saveState === "success") ? (
        <div
          aria-live={saveState === "error" ? "assertive" : "polite"}
          className={cn(
            "rounded-[1.15rem] border px-4 py-3 text-sm",
            saveState === "error"
              ? "border-destructive/35 bg-destructive/5 text-destructive"
              : "border-emerald-300/40 bg-emerald-50 text-emerald-700",
          )}
          role={saveState === "error" ? "alert" : "status"}
        >
          {saveState === "error"
            ? t(saveError ?? "timeline.messages.saveError")
            : t("timeline.editor.saveSuccess")}
        </div>
      ) : null}

      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-3">
          <Label className="text-[1.05rem] font-semibold text-foreground" htmlFor="timeline-era-label">
            {t("timeline.fields.eraLabel")}
          </Label>
          <Input
            className="h-auto rounded-none border-0 border-b border-border/85 bg-transparent px-0 py-3 text-[1.1rem] shadow-none focus-visible:ring-0"
            id="timeline-era-label"
            onChange={(nextEvent) =>
              setDraft((current) => ({ ...current, eraLabel: nextEvent.target.value }))
            }
            placeholder={t("timeline.fields.eraLabelPlaceholder")}
            value={draft.eraLabel}
          />
        </div>

        <div className="space-y-3">
          <Label className="text-[1.05rem] font-semibold text-foreground" htmlFor="timeline-title">
            {t("timeline.fields.title")}
          </Label>
          <Input
            autoFocus
            className="h-auto rounded-none border-0 border-b border-border/85 bg-transparent px-0 py-3 text-[1.1rem] shadow-none focus-visible:ring-0"
            id="timeline-title"
            onChange={(nextEvent) =>
              setDraft((current) => ({ ...current, title: nextEvent.target.value }))
            }
            placeholder={t("timeline.fields.titlePlaceholder")}
            value={draft.title}
          />
        </div>
      </div>

      <div className="space-y-3 pb-2">
        <Label className="text-[1.05rem] font-semibold text-foreground" htmlFor="timeline-description">
          {t("timeline.fields.description")}
        </Label>
        <textarea
          className={textareaClassName}
          id="timeline-description"
          onChange={(nextEvent) =>
            setDraft((current) => ({
              ...current,
              description: nextEvent.target.value,
            }))
          }
          placeholder={t("timeline.fields.descriptionPlaceholder")}
          value={draft.description}
        />
      </div>

      <section className="space-y-5 border-b border-border/85 pb-8">
        <div className="space-y-1">
          <h3 className="text-[1.15rem] font-semibold text-foreground">{t("timeline.images.title")}</h3>
          <p className="text-sm leading-7 text-muted-foreground">{t("timeline.images.description")}</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => void onGenerateImage()} type="button">
            <Sparkles className="h-4 w-4" />
            {t("story.attachments.generateIllustration")}
          </Button>
          {imageAttachments.length ? (
            <Button onClick={() => void onRemoveImage()} type="button" variant="outline">
              <Trash2 className="h-4 w-4" />
              {t("story.attachments.removeImage")}
            </Button>
          ) : null}
        </div>

        {imageAttachments.length && imageAttachments[0]?.imageUrl ? (
          <div className="overflow-hidden rounded-[1.6rem] border border-white/70 bg-white/75">
            <img
              alt={imageAttachments[0].fileName}
              className="h-[18rem] w-full object-cover"
              src={imageAttachments[0].imageUrl}
            />
          </div>
        ) : (
          <div className="rounded-[1.5rem] border border-dashed border-border/75 bg-secondary/22 px-5 py-6 text-sm text-muted-foreground">
            {t("timeline.images.empty")}
          </div>
        )}
      </section>

      <section className="space-y-5 border-b border-border/85 pb-8">
        <div className="space-y-1">
          <h3 className="text-[1.15rem] font-semibold text-foreground">{t("timeline.links.title")}</h3>
          <p className="text-sm leading-7 text-muted-foreground">{t("timeline.links.body")}</p>
        </div>

        <TimelineLinkPicker onAddLink={onAddLink} options={linkOptions} />

        {event.links.length === 0 ? (
          <div className="rounded-[1.3rem] border border-dashed border-border/80 px-4 py-4 text-sm text-muted-foreground">
            {t("timeline.links.empty")}
          </div>
        ) : (
          <ul className="space-y-0">
            {event.links.map((link, index) => (
              <li
                className={cn(
                  "space-y-3 border-border/70 px-0 py-4",
                  index < event.links.length - 1 && "border-b",
                )}
                key={link.id}
              >
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t(link.linkedKindLabelKey)}
                  </p>
                  <p className="text-base font-semibold text-foreground">{link.label}</p>
                  <p className="text-sm text-muted-foreground">{link.secondaryText}</p>
                  {link.broken && link.repairHintKey ? (
                    <p className="inline-flex items-center gap-2 text-sm text-destructive">
                      <TriangleAlert className="h-4 w-4" />
                      {t(link.repairHintKey)}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {link.openHref ? (
                    <Button asChild type="button" variant="outline">
                      <Link to={link.openHref}>
                        <Link2 className="h-4 w-4" />
                        {t("timeline.links.openAction")}
                      </Link>
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => void onRemoveLink(link.id)}
                    type="button"
                    variant="ghost"
                  >
                    <Unlink className="h-4 w-4" />
                    {t("timeline.links.removeAction")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="flex flex-wrap justify-end gap-3">
        <Button onClick={() => void handleSave()} type="button">
          <Save className="h-4 w-4" />
          {saveState === "saving"
            ? t("timeline.actions.saving")
            : t("timeline.actions.save")}
        </Button>
      </div>
    </section>
  );
}
